import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { countries } from "countries-list";
import YouTubeVideoRankContent from "../Contents/YtVideoRankChecker";
import { Card, CardContent, Typography, Divider } from "@mui/material";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

const getFlagEmoji = (countryCode) => {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
};

function YouTubeVideoRankChecker({ user, login, isLoggedIn }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("US");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resultData, setResultData] = useState(null);
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  const countriesList = Object.entries(countries)
    .map(([code, country]) => ({ code, name: country.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    const secondaryInputText = localStorage.getItem("secondaryInputText");
    const primaryDropDown = localStorage.getItem("primary-drop-down");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
    if (secondaryInputText) {
      setKeyword(secondaryInputText);
      localStorage.removeItem("secondaryInputText");
    }
    if (primaryDropDown) {
      setCountry(primaryDropDown);
      localStorage.removeItem("primary-drop-down");
    }
  }, []);

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    if (!videoUrl || !keyword) {
      setError("Both video URL and keyword are required.");
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to view YouTube video rank.");
      return;
    }

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+/;
    const isValid =
      youtubeRegex.test(videoUrl) &&
      (videoUrl.includes("v=") ||
        videoUrl.includes("youtu.be/") ||
        videoUrl.includes("/shorts/"));
    if (!isValid) {
      setError(
        "Please enter a valid YouTube video URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      return;
    }

    setLoading(true);
    setResultData(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/video-rank-checker`,
        {
          youtubeVideoUrl: videoUrl,
          youtubeKeyword: keyword,
          selectCountry: country,
        },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;
      setResultData(data.rankInfo);
      setSuccess(data.successMessage);
      setCredits({
        remaining: data.remaining_credits,
        availed: data.availed_credits,
        total: data.total_credits,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.errorMessage || "Failed to fetch rank info."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            YouTube Video Rank Checker
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter a YouTube video URL and keyword to check your video ranking in
            search results
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="YouTube Video URL"
              variant="outlined"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
              size="medium"
              sx={{ mb: 2 }}
              name="primary-textbox"
            />

            <TextField
              fullWidth
              label="Search Keyword"
              variant="outlined"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. how to grow on YouTube"
              size="medium"
              name="secondary-textbox"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel id="country-select-label">Country</InputLabel>
              <Select
                labelId="country-select-label"
                value={country}
                label="Country"
                onChange={(e) => setCountry(e.target.value)}
                name="primary-drop-down"
              >
                {countriesList.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              Check Video Rank
            </StyledButton>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b> 💡 1 Video Video Rank Check = 1 Credit</b>
              <br></br>Enter Video URL, Keyword and select country to view your
              video rank.Have questions?{" "}
              <Link to="/contact">Contact support</Link>
            </Typography>
          </Box>
          {resultData && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom align="center">
                Video Rank Result
              </Typography>
              <Card variant="outlined" sx={{ maxWidth: 600, margin: "0 auto" }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Keyword & Country
                  </Typography>
                  <Box
                    display="flex"
                    // justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body1">
                      {resultData.keyword}
                    </Typography>
                    <Typography variant="body2" fontSize="1.5rem">
                      {getFlagEmoji(country)}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Rank Position
                  </Typography>
                  <Typography
                    variant="h5"
                    color={resultData.rank ? "success.main" : "text.disabled"}
                  >
                    {resultData.rank ? `#${resultData.rank}` : "Not Ranked"}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </ContentBox>
        <CommentSection
          toolId="youtube/video-rank-checker"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeVideoRankContent />
      </PageContainer>
    </>
  );
}

export default YouTubeVideoRankChecker;
